import type { ReactNode } from 'react'
import { Button, Paper, Table, Text } from '@mantine/core'
import type { OrpcClient } from '@/orpc/client'
import { ACCOUNTING_DETAILS_CAP, type AccountingReport } from '@/model/middleware-types'
import { BILLING_STATUS_LABELS, isBillingStatusValue } from '@/pages/Billing/utils'
import { formatCalendarDate, formatCents, formatRoutingLabel } from '../utils'

export type DetailRow = Awaited<ReturnType<OrpcClient['billingV2Report']>>['details'][number]

const COMMON_COLUMNS = [
  'Encounter / claim',
  'Patient',
  'Service date',
  'PC / legal entity',
  'Encounter state',
  'Provider',
  'Payer',
]

const VIEW_COLUMNS: Record<AccountingReport, string[]> = {
  revenue: [
    'Routing',
    'Claim status',
    'Charges',
    'Contractual',
    'Other write-offs',
    'Charges after adjustments',
  ],
  payments: [
    'Transaction / reference',
    'Payment type / activity',
    'Payment date',
    'Method',
    'Cash amount',
  ],
  outstanding: ['Age (days)', 'Insurance A/R', 'Patient A/R', 'Total A/R'],
}

function claimStatusLabel(status: string): string {
  return isBillingStatusValue(status) ? BILLING_STATUS_LABELS[status] : status
}

function viewCells(row: DetailRow): ReactNode {
  if ('transactionId' in row) {
    return (
      <>
        <Table.Td>
          <div>{row.transactionId}</div>
          <Text size="xs" c="dimmed">
            {row.reference ?? '—'}
          </Text>
        </Table.Td>
        <Table.Td>{row.activity}</Table.Td>
        <Table.Td>{formatCalendarDate(row.paymentDate)}</Table.Td>
        <Table.Td>{row.method.label}</Table.Td>
        <Table.Td ta="right">{formatCents(row.cashCents)}</Table.Td>
      </>
    )
  }

  if ('ageDays' in row) {
    return (
      <>
        <Table.Td ta="right">{row.ageDays}</Table.Td>
        <Table.Td ta="right">{formatCents(row.insuranceArCents)}</Table.Td>
        <Table.Td ta="right">{formatCents(row.patientArCents)}</Table.Td>
        <Table.Td ta="right">{formatCents(row.totalArCents)}</Table.Td>
      </>
    )
  }

  return (
    <>
      <Table.Td>{formatRoutingLabel(row.submissionRoute)}</Table.Td>
      <Table.Td>{claimStatusLabel(row.claimStatus)}</Table.Td>
      <Table.Td ta="right">{formatCents(row.chargesCents)}</Table.Td>
      <Table.Td ta="right">{formatCents(row.contractualCents)}</Table.Td>
      <Table.Td ta="right">{formatCents(row.otherWriteoffsCents)}</Table.Td>
      <Table.Td ta="right">{formatCents(row.afterAdjustmentsCents)}</Table.Td>
    </>
  )
}

function rowKey(row: DetailRow): string {
  return 'transactionId' in row
    ? `${row.encounterId}:${row.transactionId}:${row.activity}`
    : row.encounterId
}

export function DetailsTable({
  report,
  title,
  rows,
  truncated,
  showAllVisible,
  onShowAll,
  onOpenEncounter,
}: {
  report: AccountingReport
  title: string
  rows: DetailRow[]
  truncated: boolean
  showAllVisible: boolean
  onShowAll: () => void
  onOpenEncounter: (encounterId: string) => void
}) {
  const columns = [...COMMON_COLUMNS, ...VIEW_COLUMNS[report]]

  return (
    <Paper withBorder radius="md" className="overflow-hidden p-4 shadow-sm">
      <div className="mb-3 flex items-center justify-between gap-3">
        <Text component="h3" fw={600}>
          {title}
        </Text>
        {showAllVisible && (
          <Button variant="subtle" size="xs" onClick={onShowAll}>
            Show all filtered details
          </Button>
        )}
      </div>

      <Table.ScrollContainer minWidth={1200}>
        <Table horizontalSpacing="sm" verticalSpacing="xs">
          <Table.Thead className="bg-[#F8FBFF]">
            <Table.Tr>
              {columns.map((column) => (
                <Table.Th key={column}>{column}</Table.Th>
              ))}
            </Table.Tr>
          </Table.Thead>

          <Table.Tbody>
            {rows.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={columns.length}>
                  <Text size="sm" c="dimmed" ta="center">
                    No records match these filters.
                  </Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              rows.map((row) => (
                <Table.Tr key={rowKey(row)}>
                  <Table.Td>
                    <button
                      type="button"
                      className="cursor-pointer text-left text-blue-600 hover:underline"
                      onClick={() => onOpenEncounter(row.encounterId)}
                    >
                      {row.encounterId}
                    </button>
                    <Text size="xs" c="dimmed">
                      {row.claimId ?? '—'}
                    </Text>
                  </Table.Td>
                  <Table.Td>{row.patientName}</Table.Td>
                  <Table.Td>{formatCalendarDate(row.serviceDate)}</Table.Td>
                  <Table.Td>{row.entity.label}</Table.Td>
                  <Table.Td>{row.state.label}</Table.Td>
                  <Table.Td>{row.provider.label}</Table.Td>
                  <Table.Td>{row.payer.label}</Table.Td>
                  {viewCells(row)}
                </Table.Tr>
              ))
            )}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      {truncated && (
        <Text size="xs" c="dimmed" mt="xs">
          Showing the first {ACCOUNTING_DETAILS_CAP.toLocaleString()} records — the CSV export
          includes all of them.
        </Text>
      )}
    </Paper>
  )
}
